import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { api, type Sample } from '../api';
import CurvePanel from '../components/CurvePanel';
import PidStage from '../components/PidStage';
import Timeline from '../components/Timeline';
import { useMe } from '../../../hooks';
import { useDataset, useSamples } from '../hooks';
import { formatDate, formatDuration, formatRows, formatTime } from '../../../shared/format';
import { canPresent } from '../../../shared/persona';
import { clearHostToken, loadHostToken, saveHostToken, useRoomSession } from '../lib/room';

function initialCenter(startedAt: string | null, endedAt: string | null): string | null {
  if (!startedAt) return null;
  const start = new Date(startedAt).getTime();
  const end = endedAt ? new Date(endedAt).getTime() : start;
  return new Date(Math.min(end, start + 150_000)).toISOString();
}

export default function Replay() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const detailQuery = useDataset(id);
  const dataset = detailQuery.data?.dataset;
  const me = useMe();

  const roomCode = searchParams.get('room');
  const hostToken = useMemo(() => (roomCode ? loadHostToken(roomCode) : null), [roomCode]);
  const room = useRoomSession(roomCode, hostToken);
  const roomActive = Boolean(roomCode);
  const roomState = room.state;

  const [cursor, setCursor] = useState<string | null>(null);
  const [bufferCenter, setBufferCenter] = useState<string | null>(null);
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState(1);
  const [zoom, setZoom] = useState(1);
  const [selected, setSelected] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [roomError, setRoomError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const canCreateRoom = Boolean(
    me.data && canPresent(me.data.persona) && !me.data.is_read_only,
  );
  const canControl = !roomActive || (room.isHost && room.status === 'open');

  useEffect(() => {
    if (!dataset) return;
    if (roomActive) return;
    setCursor(dataset.startedAt);
    setBufferCenter(initialCenter(dataset.startedAt, dataset.endedAt));
    setPlaying(false);
    setSelected(null);
    setZoom(1);
  }, [dataset?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!roomState) return;
    setCursor(roomState.cursorTs);
    setPlaying(roomState.playing);
    setSpeed(roomState.speed);
    setZoom(roomState.viewport.zoom);
    setSelected(roomState.selectedChannel);
  }, [
    roomState?.cursorTs,
    roomState?.playing,
    roomState?.speed,
    roomState?.viewport.zoom,
    roomState?.selectedChannel,
  ]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!roomCode || !roomState || !id) return;
    if (roomState.datasetId !== id) {
      navigate(`/ccs/datasets/${roomState.datasetId}?room=${roomCode}`, { replace: true });
    }
  }, [roomCode, roomState?.datasetId, id, navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  const samplesQuery = useSamples(id, bufferCenter);
  const samples = samplesQuery.data ?? [];
  const samplesRef = useRef<Sample[]>(samples);
  const cursorRef = useRef<string | null>(cursor);

  useEffect(() => {
    samplesRef.current = samples;
  }, [samples]);

  useEffect(() => {
    cursorRef.current = cursor;
  }, [cursor]);

  const current = useMemo(() => {
    if (!samples.length) return null;
    if (!cursor) return samples[0];
    const t = new Date(cursor).getTime();
    let result = samples[0];
    for (const item of samples) {
      if (new Date(item.ts).getTime() <= t) result = item;
      else break;
    }
    return result;
  }, [samples, cursor]);

  useEffect(() => {
    if (!playing || !dataset?.startedAt || roomActive) return;
    const startMs = new Date(dataset.startedAt).getTime();
    const endMs = new Date(dataset.endedAt ?? dataset.startedAt).getTime();
    let last = performance.now();
    const timer = window.setInterval(() => {
      const now = performance.now();
      const delta = now - last;
      last = now;
      const currentMs = cursorRef.current ? new Date(cursorRef.current).getTime() : startMs;
      let nextMs = currentMs + delta * speed;
      if (nextMs >= endMs) {
        nextMs = endMs;
        setPlaying(false);
      }
      const next = new Date(nextMs).toISOString();
      setCursor(next);

      // 缓冲区按游标居中，提前量约 2.5 分钟；在剩余 1 分钟时就换窗，
      // 避免游标越过缓冲区末尾后画面数值冻结。
      const lastSample = samplesRef.current[samplesRef.current.length - 1];
      if (lastSample && nextMs > new Date(lastSample.ts).getTime() - 60_000) {
        setBufferCenter((prev) => {
          if (prev && Math.abs(new Date(next).getTime() - new Date(prev).getTime()) < 60_000) {
            return prev;
          }
          return next;
        });
      }
    }, 100);
    return () => window.clearInterval(timer);
  }, [playing, speed, dataset?.id, dataset?.startedAt, dataset?.endedAt, roomActive]);

  useEffect(() => {
    if (!roomActive || !cursor) return;
    setBufferCenter((prev) => {
      if (prev && Math.abs(new Date(cursor).getTime() - new Date(prev).getTime()) < 60_000) {
        return prev;
      }
      return cursor;
    });
  }, [roomActive, cursor]);

  if (detailQuery.isLoading) {
    return <div className="empty">加载会话…</div>;
  }

  if (detailQuery.isError || !dataset) {
    return (
      <div className="empty">
        <b>会话不存在</b>
        <Link to="/ccs">返回会话列表</Link>
      </div>
    );
  }

  const empty = dataset.rowCount === 0;
  const disabledChannels = dataset.summary?.disabledChannels ?? [];

  const patchOrLocal = (patch: Parameters<typeof room.sendPatch>[0], local: () => void) => {
    if (!roomActive) {
      local();
      return;
    }
    if (room.isHost && room.status === 'open') room.sendPatch(patch);
  };

  const togglePlay = () => patchOrLocal({ playing: !playing }, () => setPlaying((v) => !v));

  const seek = (seconds: number) => {
    if (!dataset.startedAt) return;
    const next = new Date(new Date(dataset.startedAt).getTime() + seconds * 1000).toISOString();
    if (!roomActive) {
      setCursor(next);
      setBufferCenter(next);
      return;
    }
    if (room.isHost && room.status === 'open') {
      room.sendPatch({ cursorTs: next });
      setBufferCenter(next);
    }
  };

  const changeSpeed = (value: number) => patchOrLocal({ speed: value }, () => setSpeed(value));

  const applyZoom = (value: number) => {
    const next = Math.min(4, Math.max(0.5, Number(value.toFixed(2))));
    patchOrLocal({ viewport: { zoom: next } }, () => setZoom(next));
  };

  const selectChannel = (key: string) => patchOrLocal({ selectedChannel: key }, () => setSelected(key));

  const closeCurve = () => patchOrLocal({ selectedChannel: null }, () => setSelected(null));

  const shareLink = `${window.location.origin}/ccs/datasets/${dataset.id}?room=${roomCode}`;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareLink);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      // clipboard may be unavailable over http
    }
  };

  const leaveRoom = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('room');
    setSearchParams(next, { replace: true });
  };

  const endRoom = () => {
    room.endRoom();
    if (roomCode) clearHostToken(roomCode);
    leaveRoom();
  };

  const startRoom = async () => {
    setCreating(true);
    setRoomError(null);
    try {
      const created = await api<{ shareCode: string; hostToken: string }>('/api/ccs/rooms', {
        method: 'POST',
        body: JSON.stringify({ datasetId: dataset.id, cursorTs: cursor, selectedChannel: selected }),
      });
      saveHostToken(created.shareCode, created.hostToken);
      setSearchParams({ room: created.shareCode }, { replace: true });
    } catch (err) {
      setRoomError(err instanceof Error ? err.message : '无法创建房间');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="replay">
      <div className="replay-head">
        <div>
          <Link to="/ccs" className="muted" style={{ fontSize: 12 }}>
            ← 返回会话
          </Link>
          <div className="replay-title">
            <div className="eyebrow" style={{ marginTop: 8 }}>
              Replay
            </div>
            <h1 className="mono">{dataset.sessionId}</h1>
            <div className="replay-meta">
              <span>
                {formatDate(dataset.startedAt)} {formatTime(dataset.startedAt)} →{' '}
                {formatTime(dataset.endedAt)}
              </span>
              <span>时长 {formatDuration(dataset.durationS)}</span>
              <span>{formatRows(dataset.rowCount)} 行</span>
              <span>{dataset.sourceFilename}</span>
            </div>
          </div>
        </div>
        <div className="badge-row">
          <span className="badge ok">只读</span>
          {empty ? <span className="badge warn">空会话</span> : null}
          {dataset.gapCount > 0 ? (
            <span className="badge warn">丢帧 {dataset.gapCount}</span>
          ) : null}
          {dataset.summary?.opCount === 0 && !empty ? (
            <span className="badge">无操作记录</span>
          ) : null}
          {disabledChannels.length ? (
            <span
              className="badge warn"
              title={`压缩机子系统未启用：${disabledChannels.join(', ')}`}
            >
              未启用 {disabledChannels.length} 通道
            </span>
          ) : null}
        </div>
      </div>

      {roomActive || canCreateRoom ? (
        <section className="room-bar">
          {roomActive ? (
            <>
              <span className={`room-dot${room.hostOnline ? ' on' : ''}`} aria-hidden />
              <span className="room-code">
                房间码 <b className="mono">{roomCode}</b>
              </span>
              <span className="room-role">
                {room.status === 'connecting'
                  ? '连接中…'
                  : room.status === 'closed'
                    ? '房间已结束'
                    : room.isHost
                      ? '主持人'
                      : `跟随 ${room.hostName ?? '主持人'}`}
              </span>
              {room.status === 'open' ? (
                <span className={`badge ${room.hostOnline ? 'ok' : 'warn'}`}>
                  {room.hostOnline ? '主持人在线' : '等待主持人'}
                </span>
              ) : null}
              {!room.isHost && room.status === 'open' ? <span className="badge">只读跟随</span> : null}
              <div className="spacer" />
              <button type="button" className="btn btn-ghost btn-sm" onClick={copyLink}>
                {copied ? '已复制链接' : '复制分享链接'}
              </button>
              {room.isHost ? (
                <button type="button" className="btn btn-sm" onClick={endRoom}>
                  结束演示
                </button>
              ) : (
                <button type="button" className="btn btn-ghost btn-sm" onClick={leaveRoom}>
                  离开房间
                </button>
              )}
            </>
          ) : (
            <>
              <span className="room-role">把当前回放同步给同事，一起看同一时刻</span>
              <div className="spacer" />
              {roomError ? <span className="room-error">{roomError}</span> : null}
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={startRoom}
                disabled={creating}
              >
                {creating ? '创建中…' : '发起演示房间'}
              </button>
            </>
          )}
        </section>
      ) : null}

      <section className="panel pid-panel">
        <div className="panel-toolbar">
          <div className="zoom-group">
            <button
              type="button"
              onClick={() => applyZoom(zoom - 0.25)}
              disabled={!canControl}
              title="缩小"
            >
              −
            </button>
            <span>{Math.round(zoom * 100)}%</span>
            <button
              type="button"
              onClick={() => applyZoom(zoom + 0.25)}
              disabled={!canControl}
              title="放大"
            >
              ＋
            </button>
            <button type="button" className="wide" onClick={() => applyZoom(1)} disabled={!canControl} title="适应宽度">
              适应
            </button>
          </div>
          <span className="badge" style={{ background: '#F0B0B1', color: '#0b1220' }}>
            温度
          </span>
          <span className="badge" style={{ background: '#B0DDC1', color: '#0b1220' }}>
            流量 / 液位
          </span>
          <span className="badge" style={{ background: '#AAD3F5', color: '#0b1220' }}>
            压力
          </span>
          <div className="spacer" />
          {samplesQuery.isFetching ? (
            <span className="muted" style={{ fontSize: 11 }}>
              加载窗口…
            </span>
          ) : (
            <span className="muted" style={{ fontSize: 11 }}>
              {canControl ? '点击图上数值查看 5 分钟曲线' : '跟随主持人视图 · 只读'}
            </span>
          )}
        </div>
        <PidStage
          sample={current}
          zoom={zoom}
          selected={selected}
          onSelect={canControl ? selectChannel : () => undefined}
        />
      </section>

      <Timeline
        startedAt={dataset.startedAt}
        endedAt={dataset.endedAt}
        durationS={dataset.durationS}
        cursor={cursor}
        playing={playing}
        speed={speed}
        sample={current}
        readOnly={!canControl}
        onToggle={togglePlay}
        onSeek={seek}
        onSpeed={changeSpeed}
      />

      {selected ? (
        <CurvePanel
          datasetId={dataset.id}
          channelKey={selected}
          cursor={cursor}
          playing={playing}
          closable={canControl}
          onClose={closeCurve}
        />
      ) : null}
    </div>
  );
}